import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import {
  HStack,
  CloseIcon,
  IconButton,
  Cascader,
  type CascaderOption,
  type CascaderProps,
  type CascaderComponentProps,
  CascaderComponents,
  CascaderUtilityComponents,
  Text,
  ChevronRightIcon,
  VStack,
  Center,
  SearchIcon,
  Heading,
  Box,
  Flex,
  Alert,
  AlertDescription,
  AlertIcon,
  OutlineCircleInfoIcon,
} from '@frontend/components';
import { noop } from '@frontend/utils';

import {
  ICON_STYLES,
  isCountHelperSubmenuKey,
  isHelperKey,
} from '../utils/cascader';
import { type FormStateData } from './RulesDetailModal';

type IconCascaderOption = CascaderOption<{
  icon?: React.ReactNode;
  description?: string | null;
  warning?: string | null;
  isOptional?: boolean;
}>;

const CustomContainer = ({
  children,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['Container']) => {
  return (
    <CascaderComponents.Container {...rest} flexGrow={1}>
      {children}
    </CascaderComponents.Container>
  );
};

const IconLeafOption = ({
  optionRef,
  option,
  getCommonProps: _,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['LeafOption']) => {
  return (
    <HStack ref={optionRef} pl={option.icon ? 1 : 1.5} {...rest}>
      {option.icon}
      <Text size="md" as="span" flexGrow={1} textAlign="left">
        {option.label}
      </Text>
    </HStack>
  );
};

const IconSubmenuOption = ({
  optionRef,
  option,
  getCommonProps: _,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['SubmenuOption']) => {
  return (
    <HStack ref={optionRef} spacing={0.5} pl={1.5} {...rest}>
      {option.icon}
      <Text size="md" as="span" flexGrow={1} textAlign="left">
        {option.label}
      </Text>
      <ChevronRightIcon {...ICON_STYLES} h={2} w={2} ml={0.5} />
    </HStack>
  );
};

const signalSelectMenuContainerCss = {
  display: 'flex',
  flexDirection: 'column',
  bg: 'page.surface',
  border: '1px solid',
  borderColor: 'separator.border',
  borderRadius: 'md',
  shadow: 'popover',
  zIndex: 'popover',
  minWidth: 'var(--reference-width)',
  width: 'max-content',
  maxWidth: '800px',
};

const signalSelectMenuInnerRowCss = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'scroll',
  scrollbarWidth: 'none',
};

const SignalSelectPreviewDescription = ({
  description,
}: {
  description?: string | null;
}) => {
  if (!description) {
    return null;
  }
  return (
    <Flex gap={1}>
      <OutlineCircleInfoIcon
        color="feature.secondary"
        boxSize={2.5}
        position="relative"
        top={0.125}
      />
      <Text size="sm" color="feature.primary">
        {description}
      </Text>
    </Flex>
  );
};

const SignalSelectPreviewWarning = ({
  warning,
}: {
  warning?: string | null;
}) => {
  if (!warning) {
    return null;
  }
  return (
    <Alert status="warning" size="sm">
      <AlertIcon />
      <AlertDescription>{warning}</AlertDescription>
    </Alert>
  );
};

const MenuWrapper = ({
  children,
  menuRef,
  __css,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['MenuWrapper']) => {
  const { getCommonProps } = rest;
  const { getPreviewedOption } = getCommonProps();
  const previewOption = getPreviewedOption();

  return (
    <CascaderComponents.MenuWrapper
      {...rest}
      __css={signalSelectMenuContainerCss}
    >
      <Box __css={signalSelectMenuInnerRowCss} ref={menuRef}>
        {children}
      </Box>
      {previewOption &&
        (previewOption.description || previewOption.warning) && (
          <Flex
            __css={{
              contain: 'inline-size', // so the size of the text does not influence the parent
            }}
            display="flex"
            flexDirection="column"
            px={1.5}
            py={1}
            bg="page.plateLight"
            borderTop="1px solid"
            borderColor="separator.divider"
            gap={1}
          >
            <SignalSelectPreviewDescription
              description={previewOption.description}
            />
            <SignalSelectPreviewWarning warning={previewOption.warning} />
          </Flex>
        )}
    </CascaderComponents.MenuWrapper>
  );
};

const useIconAtPath = (
  fullPath: string[],
  getOptionAtPath: (path: string[]) => IconCascaderOption | undefined,
) => {
  const icon = useMemo(() => {
    // If the last value is a helper key, we need to get the option at the path before the helper key
    const lastIdx = fullPath.length - 1;
    const secondToLastIdx = fullPath.length - 2;
    if (isHelperKey(fullPath[lastIdx])) {
      const pathBeforeHelper = fullPath.slice(0, lastIdx);
      const parentOption = getOptionAtPath(pathBeforeHelper);
      return parentOption?.icon;
    } else if (isCountHelperSubmenuKey(fullPath[secondToLastIdx])) {
      const pathBeforeHelper = fullPath.slice(0, secondToLastIdx);
      const parentOption = getOptionAtPath(pathBeforeHelper);
      return parentOption?.icon;
    }
    return undefined;
  }, [fullPath, getOptionAtPath]);
  return icon;
};

const IconSelectedValueContainer = ({
  children,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['SelectedValueContainer']) => {
  const { getCommonProps } = rest;
  const {
    getSelectedValue,
    getSelectedOption,
    getOptionAtPath,
    getInputValue,
  } = getCommonProps();
  const selectedValue = getSelectedValue();
  const inputValue = getInputValue();
  const selectedOption = getSelectedOption();
  const pathIcon = useIconAtPath(selectedValue, getOptionAtPath);
  const icon = pathIcon ?? selectedOption?.icon;
  const showInputValueIndicator = Boolean(inputValue.length > 0 || icon);

  return (
    <CascaderComponents.SelectedValueContainer
      pl={icon ? 1 : undefined}
      {...rest}
    >
      {showInputValueIndicator && (
        <Center pr={0.5}>
          {inputValue.length > 0 ? (
            <SearchIcon boxSize="calc(100% * 2 / 3)" {...ICON_STYLES} />
          ) : (
            icon
          )}
        </Center>
      )}
      {children}
    </CascaderComponents.SelectedValueContainer>
  );
};

const IconSingleSelectedValue = ({
  children,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['SingleSelectedValue']) => {
  const { getCommonProps, selectedOption: option } = rest;
  const { getOptionAtPath } = getCommonProps();
  const pathIcon = useIconAtPath(option.fullPath, getOptionAtPath);
  const icon = option.icon ?? pathIcon;

  return (
    <CascaderComponents.SingleSelectedValue
      {...rest}
      left={icon ? 3.5 : undefined}
    >
      {children}
    </CascaderComponents.SingleSelectedValue>
  );
};

const IconSearchOption = ({
  option,
  getCommonProps,
  words,
  lastOptionLabel,
  parentPath,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['SearchOption']) => {
  const { getOptionAtPath } = getCommonProps();
  const pathIcon = useIconAtPath(option.fullPath, getOptionAtPath);
  const icon = option.icon ?? pathIcon;

  return (
    <HStack {...rest}>
      {icon}
      <VStack flexGrow={1} align="start" gap={0}>
        <CascaderUtilityComponents.SearchOptionLabel
          label={lastOptionLabel}
          words={words}
        />
        <CascaderUtilityComponents.SearchOptionPath
          path={parentPath}
          words={words}
        />
      </VStack>
    </HStack>
  );
};

const IconSubmenuHeader = ({
  submenu,
  getCommonProps: _,
  ...rest
}: CascaderComponentProps<IconCascaderOption>['SubmenuHeader']) => {
  return (
    <HStack spacing={0.25} pl={submenu.icon ? 1 : 1.5} {...rest}>
      {submenu.icon}
      <Heading
        size="sm"
        as="span"
        flexGrow={1}
        textAlign="left"
        color="feature.secondary"
        textTransform="uppercase"
      >
        {submenu.label}
      </Heading>
    </HStack>
  );
};

const customComponents = {
  Container: CustomContainer,
  LeafOption: IconLeafOption,
  SubmenuOption: IconSubmenuOption,
  SearchOption: IconSearchOption,
  SingleSelectedValue: IconSingleSelectedValue,
  SelectedValueContainer: IconSelectedValueContainer,
  SubmenuHeader: IconSubmenuHeader,
  MenuWrapper: MenuWrapper,
};

export type SignalSelectOptionType = IconCascaderOption;

type SignalSelectProps = CascaderProps<SignalSelectOptionType> & {
  inputRef?: React.RefObject<HTMLInputElement>;
  onClearSignal?: () => void;
  isClearable?: boolean;
  signalIndex?: number;
  groupIndex?: number;
  queryIndex?: number;
  hideRemoveButton?: boolean;
  // Props for controlling cascader open state
  open?: boolean;
  onPopupVisibleChange?: (open: boolean) => void;
};

// Positioning configuration to disable flipping
// The menu will always open below the control to prevent flip loop issues
const cascaderPositioning = {
  placement: 'bottom-start' as const,
  strategy: 'fixed' as const,
  flip: false, // Disable flipping to prevent description/warning feedback loop
};

export const SignalSelect = (props: SignalSelectProps) => {
  const {
    inputRef,
    onClearSignal = noop,
    isDisabled,
    isClearable = false,
    signalIndex,
    groupIndex,
    queryIndex,
    hideRemoveButton,
    onPopupVisibleChange,
    ...rest
  } = props;

  const { getFieldProps } = useFormikContext<FormStateData>();

  // Get field props for the signal if we have an index
  // Dynamically build the field name based on the existence of signalIndex, groupIndex, and queryIndex
  // TODO: still getting a formik handleBlur warning
  let fieldName: string | undefined;
  if (
    signalIndex !== undefined &&
    groupIndex !== undefined &&
    queryIndex !== undefined
  ) {
    fieldName = `signals.${signalIndex}.groups.${groupIndex}.queries.${queryIndex}.fieldName`;
  }

  const fieldProps = fieldName ? getFieldProps(fieldName) : {};

  return (
    <HStack
      align="stretch"
      alignItems="center"
      flexGrow={1}
      spacing={1}
      className="signal-select-container"
    >
      <Cascader
        inputRef={inputRef}
        {...fieldProps}
        isDisabled={isDisabled}
        menuModePositioning={cascaderPositioning}
        searchModePositioning={cascaderPositioning}
        {...rest}
        // Clearing happens at the signal level not the cascader level in this UX
        isClearable={false}
        className="signal-select-cascader"
        components={customComponents}
      />
      {isClearable && !isDisabled && !hideRemoveButton && (
        <IconButton
          size="sm"
          m={0}
          onClick={onClearSignal}
          className="signal-select-clear-button"
        >
          <CloseIcon />
        </IconButton>
      )}
    </HStack>
  );
};
SignalSelect.displayName = 'SignalSelect';
